import { AccessDeniedError } from '@adornis/abac/errors/AccessDeniedError.js';
import { logger } from '@adornis/base/logging.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { loadAndAssert } from '../authz/access.js';
import { AssignmentProposalMessage, AssistantMessage, Conversation } from '../conversation.js';

/** A proposal may only stay one or be finalized into a plain assistant message — never e.g. a UserMessage. */
const ALLOWED_FINAL_CLASSES = [AssignmentProposalMessage._class, AssistantMessage._class];

export interface AssignmentFinalContentArgs {
  schuelerabgaben?: string[];
  musterloesung?: string;
  bewertungsschema?: string;
  leereKlausur?: string;
}

export const buildAssignmentFinalContent = (baseContent: string, args: AssignmentFinalContentArgs): string => {
  const lines = [
    Array.isArray(args.schuelerabgaben) && args.schuelerabgaben.length > 0
      ? `- Schülerabgaben: ${args.schuelerabgaben.join(', ')}`
      : null,
    args.musterloesung ? `- Musterlösung: ${args.musterloesung}` : null,
    args.bewertungsschema ? `- Bewertungsschema: ${args.bewertungsschema}` : null,
    args.leereKlausur ? `- Leere Klausur: ${args.leereKlausur}` : null,
  ].filter((line): line is string => Boolean(line));

  const trimmedBaseContent = String(baseContent || '').trim();
  if (!trimmedBaseContent) {
    return lines.join('\n');
  }

  return lines.length > 0 ? `${trimmedBaseContent}\n${lines.join('\n')}` : trimmedBaseContent;
};

export const finalizeLatestAssignmentProposal = async (
  conversationId: string,
  latestMessages?: any[],
): Promise<void> => {
  const applyPatchToMessage = (msg: any) => {
    // Do NOT modify the visible content on finalization. Mark the proposal inactive
    // and persist structured fields from params (if provided). This keeps the
    // message class intact while letting the UI render inactive proposals as normal assistant text.
    msg.active = false;
  };

  if (Array.isArray(latestMessages)) {
    const localTarget = latestMessages
      .slice()
      .reverse()
      .find(
        m =>
          m?._class === AssignmentProposalMessage._class &&
          (m?.active === true ||
            typeof m?.active === 'undefined' ||
            m?.confirmed === false ||
            typeof m?.confirmed === 'undefined'),
      );
    if (localTarget) {
      applyPatchToMessage(localTarget);
    }
  }

  try {
    const conversationCollection = await getCollection(Conversation);
    const conv = await conversationCollection.findOne({ _id: conversationId });
    if (!conv || !Array.isArray((conv as any).messages)) return;

    const messages = ((conv as any).messages as any[]) || [];
    const target = messages
      .slice()
      .reverse()
      .find(
        m =>
          m?._class === AssignmentProposalMessage._class &&
          (m?.active === true ||
            typeof m?.active === 'undefined' ||
            m?.confirmed === false ||
            typeof m?.confirmed === 'undefined'),
      );

    if (!target?._id) return;

    const setObj: any = {};
    // mark proposal inactive instead of changing visible content
    setObj['messages.$[target].active'] = false;

    await conversationCollection.updateOne(
      { _id: conversationId },
      {
        $set: setObj,
      },
      {
        arrayFilters: [
          {
            'target._id': target._id,
            'target._class': AssignmentProposalMessage._class,
          },
        ],
      },
    );
  } catch (err) {
    logger.error({ err }, 'Failed to finalize AssignmentProposalMessage');
  }
};

export const updateAssignmentProposalMessageMutation = registerMutation({
  operationName: 'updateAssignmentProposalMessageMutation',
  type: () => Boolean,
  params: [
    { name: 'conversationId', type: () => String },
    { name: 'messageId', type: () => String },
    { name: 'content', type: () => String },
    { name: 'schuelerabgaben', type: () => [String] },
    { name: 'musterloesung', type: () => String },
    { name: 'bewertungsschema', type: () => String },
    { name: 'leereKlausur', type: () => String },
    { name: 'active', type: () => Boolean },
    { name: 'finalClass', type: () => String },
  ],
  resolve:
    (
      conversationId: string,
      messageId: string,
      content: string,
      schuelerabgaben: string[],
      musterloesung: string,
      bewertungsschema: string,
      leereKlausur: string,
      active: boolean,
      finalClass: string,
    ) =>
    async () => {
      await loadAndAssert(Conversation, conversationId, 'update', { _id: 1 });
      if (typeof finalClass === 'string' && finalClass.trim() && !ALLOWED_FINAL_CLASSES.includes(finalClass.trim())) {
        throw new AccessDeniedError('Access denied: message class not allowed', { reason: 'finalClass not allowed' });
      }
      try {
        const conversationCollection = await getCollection(Conversation);
        const result = await conversationCollection.updateOne(
          { _id: conversationId },
          {
            $set: {
              'messages.$[target].content': content,
              'messages.$[target].schuelerabgaben': Array.isArray(schuelerabgaben) ? schuelerabgaben : [],
              'messages.$[target].musterloesung': musterloesung || undefined,
              'messages.$[target].bewertungsschema': bewertungsschema || undefined,
              'messages.$[target].leereKlausur': leereKlausur || undefined,
              'messages.$[target].active': Boolean(active),
              ...(typeof finalClass === 'string' && finalClass.trim()
                ? { 'messages.$[target]._class': finalClass.trim() }
                : {}),
            },
          },
          {
            arrayFilters: [
              {
                'target._id': messageId,
                'target._class': AssignmentProposalMessage._class,
              },
            ],
          },
        );

        return ((result as any)?.modifiedCount ?? 0) > 0;
      } catch (err) {
        logger.error({ err }, 'Failed to update AssignmentProposalMessage');
        return false;
      }
    },
});
